import { Button, Grid, useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { get as _get } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { createAutoService } from '../../../api/createServiceApi';
import { getPhotoDocuments } from '../../../api/getPhotoDocumentsApi';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import {
  clearClaimPhotosState,
  updateClaimCvpRefNumState,
  updateClaimPhotosState,
} from '../../../app/store/ClaimDetails/ClaimDetailSlice';
import {
  getAuthKey,
  getClaimNumber,
  getClaimPhotoInfo,
  getCvpEligible,
  getCvpRefNumber,
  getInsuredLastName,
  getPhotoUploadCCCFlag,
  getSessionId,
  getUrlKey,
} from '../../../app/store/combinedSelectors';
import * as ContextRouterActions from '../../../app/store/ContextRouter/ContextRouterActions';
import { useActions } from '../../../app/store/useActions';
import { ClaimServicesConstants, uploadPhotosBtn } from '../../../Constants/Constants';
import { CreateAutoServiceRequest } from '../../../model/create-auto-service-request';
import { CreateServiceResponse, PhotoInfo } from '../../../types/type';
import { DatadogErrorTracker, FeatureAreas } from '../../../utils/DatadogErrorTracker';
import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import ClaimDetailIndividualPhoto from './ClaimDetailIndividualPhoto';

export default function ClaimDetailPhotos(props: any) {
  const theme = useTheme();
  const photosInfo = useSelector(getClaimPhotoInfo);
  const cvpRefNum = useSelector(getCvpRefNumber);
  const cvpEligible = useSelector(getCvpEligible);
  const insuredLastName = useSelector(getInsuredLastName);
  const claimNumber = useSelector(getClaimNumber);
  const urlKey = useSelector(getUrlKey);
  const sessionId = useSelector(getSessionId);
  const authKey = useSelector(getAuthKey);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const vin = useAppSelector((state) => _get(state, 'claimDetail.claimSummary.autoClaimDetails[0].vin', ''));
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const contextRouterActions: typeof ContextRouterActions = useActions(ContextRouterActions);

  const photoUploadCCC_Flag = useSelector(getPhotoUploadCCCFlag);
  const [isLoading, setIsLoading] = useState(!photosInfo);

  async function routeToPhotos() {
    if (photoUploadCCC_Flag) {
      if (cvpRefNum) {
        navigate('/photos-selection', {
          state: {
            isStandalone: true,
            cvpRefNum: cvpRefNum,
            insuredLastName: insuredLastName,
            authKey,
          },
        });
      } else if (cvpEligible) {
        const response = await selectService();
        dispatch(
          updateClaimCvpRefNumState({
            cvpRefNum: response.cvpRefNum,
          })
        );
        navigate('/photos-selection', {
          state: {
            isStandalone: true,
            cvpRefNum: response,
            insuredLastName: insuredLastName,
            authKey: response.updatedToken,
          },
        });
      } else {
        navigate('/photos-selection', {
          state: {
            isStandalone: true,
            authKey,
          },
        });
      }
    } else {
      navigate('/photos-selection', {
        state: {
          isStandalone: true,
          authKey,
        },
      });
    }
  }

  async function selectService() {
    // Show LoadingIndicator
    props.setModalLoadingOpen(true);
    let cvpRefNum = '';
    let updatedToken = '';
    const postBody: CreateAutoServiceRequest = {
      claim: {
        claimNumber: claimNumber,
        vehicle: {
          vin: vin,
          serviceRequestInfo: {
            serviceRequestProviderId: '1001',
            serviceRequestCode: ClaimServicesConstants.CVP,
            serviceRequestDescription: ClaimServicesConstants.CVP,
          },
        },
      },
    };
    const response: CreateServiceResponse = await createAutoService(urlKey, sessionId, authKey, postBody);
    if ((_get(response, 'response.status.code', 0) as number) === 200) {
      if (_get(response, 'response.updatedAuthToken')) {
        updatedToken = response.response.updatedAuthToken;
        contextRouterActions.updateAuthToken(updatedToken);
      }
      const fullRef = _get(response, 'response.vehicle[0].eligibleServices[0].serviceProviderId', '').split('#');
      cvpRefNum = fullRef.length === 2 ? fullRef[1] : fullRef[0];
    }
    return { cvpRefNum, updatedToken };
  }

  // Set feature context when component mounts
  useEffect(() => {
    DatadogErrorTracker.setFeatureContext(FeatureAreas.CLAIM_DETAIL_PHOTOS, {
      claimNumber: claimNumber,
    });

    DatadogErrorTracker.trackAction('claim-detail-photos-viewed', {
      claimNumber: claimNumber,
    });

    return () => {
      DatadogErrorTracker.clearFeatureContext(['currentFeature', 'claimNumber']);
    };
  }, [claimNumber]);

  useEffect(() => {
    if (photosInfo) return;
    setIsLoading(true);
    (async () => {
      const docs = await getPhotoDocuments(claimNumber, urlKey, sessionId, authKey);
      if (docs) {
        dispatch(updateClaimPhotosState({ claimPhotos: docs }));
      }
      setIsLoading(false);
    })();
  }, [photosInfo, claimNumber, urlKey, sessionId, authKey, dispatch]);

  // Clear photos on unmount so they don't leak across claims.
  useEffect(() => {
    return () => {
      dispatch(clearClaimPhotosState());
    };
  }, [dispatch]);

  return (
    <Grid container sx={{ marginTop: '1rem' }}>
      {isLoading && (
        <Grid item xs={12} sx={{ paddingLeft: '1rem', paddingBottom: '1rem' }}>
          <LoadingIndicator message="Loading photos" />
        </Grid>
      )}
      {!isLoading && (!photosInfo || photosInfo.length === 0) && (
        <>
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ ml: '1rem' }}>
              Don't see your photos? There may be a short delay in processing. If a photo doesn't show up after a few
              minutes, it may not have been able to be processed by our system.
            </Typography>
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '2em', ml: '1rem' }}>
            <Typography variant="body1">You haven't added any photos to this claim yet. </Typography>
          </Grid>
        </>
      )}
      {!isLoading &&
        photosInfo &&
        photosInfo.length > 0 &&
        photosInfo.map((photo: PhotoInfo) => (
          <Grid key={photo.id + '-Grid'} item md={4} sm={6} xs={12} sx={{ paddingLeft: '1rem' }}>
            <ClaimDetailIndividualPhoto key={photo.id} photoInfo={photo} />{' '}
          </Grid>
        ))}

      <Grid item xs={12} sx={{ marginTop: '2em' }}>
        <Button
          onClick={routeToPhotos}
          disabled={isLoading}
          sx={{
            ...theme.custom.styles.genericPrimaryButton,
            marginLeft: isMobile ? '.5rem' : '1rem',
            marginRight: '.5rem',
            width: isMobile ? '95% !important' : theme.custom.styles.genericPrimaryButton.minWidth,
            marginBottom: '1rem',
            '&.MuiButtonBase-root:hover': {
              bgcolor: theme.custom.colors.primaryButtonHoverColor,
            },
          }}
        >
          {uploadPhotosBtn}
        </Button>
      </Grid>
    </Grid>
  );
}
